import { useEffect, useRef, useState } from "react";

const EDGES = [
  { left: "6%", top: "18%" }, { right: "6%", top: "22%" }, { left: "8%", bottom: "22%" },
  { right: "7%", bottom: "26%" }, { left: "50%", top: "8%" }, { left: "50%", bottom: "9%" },
];
const pick = (a) => a[Math.floor(Math.random() * a.length)];

// One RLI word layer: shows a word for `hold` seconds at a jittered interval. `depth` speeds it up (Deepen) or calms it (Integrate).
function useLayer(active, cfg, depthRef, source) {
  const [w, setW] = useState(null);
  const src = useRef(source); src.current = source;
  useEffect(() => {
    if (!active || !cfg) { setW(null); return; }
    let t1, t2;
    const tick = () => {
      const text = src.current();
      if (text) { setW({ text, key: Date.now(), edge: pick(EDGES), x: (Math.random() - 0.5) * 140, y: (Math.random() - 0.5) * 120 }); t1 = setTimeout(() => setW(null), cfg.hold * 1000); }
      t2 = setTimeout(tick, (cfg.every / (depthRef.current || 1)) * (0.7 + Math.random() * 0.8) * 1000);
    };
    t2 = setTimeout(tick, cfg.every * 500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [active, cfg, depthRef]);
  return w;
}

const STEADY = { current: 1 };
const CONS = "BCDFGHJKLMNPQRSTVWXZ";
const maskOf = (t) => [...t].map((c) => (c === " " ? " " : CONS[Math.floor(Math.random() * CONS.length)])).join("");

// Subtle flash layer, fixed: the word holds for ~1/30s (frame-timed), then a letter mask replaces it so it can't be consciously read.
function FlashLayer({ active, cfg, depthRef, words }) {
  const el = useRef(null);
  useEffect(() => {
    if (!active || !cfg || !words?.length || !el.current) return;
    let t, raf, alive = true;
    const node = el.current;
    const hide = () => { node.style.visibility = "hidden"; };
    const flash = () => {
      const w = pick(words);
      node.style.transform = `translate(${(Math.random() - 0.5) * 140}px, ${(Math.random() - 0.5) * 120}px)`;
      node.textContent = w; node.dataset.state = "word"; node.style.visibility = "visible";
      const t0 = performance.now();
      const frame = (now) => {
        if (!alive) return;
        if (now - t0 < cfg.hold * 1000) { raf = requestAnimationFrame(frame); return; }
        node.textContent = maskOf(w); node.dataset.state = "mask";
        t = setTimeout(() => { hide(); t = setTimeout(flash, (cfg.every / (depthRef.current || 1)) * (0.7 + Math.random() * 0.8) * 1000); }, (cfg.mask || 0.12) * 1000);
      };
      raf = requestAnimationFrame(frame);
    };
    t = setTimeout(flash, 2500);
    return () => { alive = false; clearTimeout(t); cancelAnimationFrame(raf); hide(); };
  }, [active, cfg, depthRef, words]);
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <span ref={el} data-testid="immersion-flash-word" className="font-serif text-white text-3xl sm:text-4xl uppercase tracking-[0.3em]" style={{ opacity: 0.3, visibility: "hidden" }} />
    </div>
  );
}

export default function DriftWords({ active, phases, flash, phaseRef, depthRef, layers }) {
  const phaseWords = () => pick(phases[Math.min(phaseRef.current, phases.length - 1)] || phases[0]);
  const visible = useLayer(active, layers?.visible, STEADY, phaseWords);
  const glimpse = useLayer(active, layers?.glimpse, depthRef, phaseWords);
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="immersion-words">
      {visible && (
        <div key={visible.key} className="absolute inset-x-0 bottom-[16%] flex justify-center px-8">
          <span data-testid="immersion-visible-word" className="rli-visible font-serif text-white text-xl sm:text-2xl uppercase tracking-[0.35em] text-center"
            style={{ animationDuration: `${layers.visible.hold}s` }}>{visible.text}</span>
        </div>
      )}
      {glimpse && (
        <span key={glimpse.key} data-testid="immersion-glimpse-word" className="absolute font-serif italic text-white/40 text-sm sm:text-base"
          style={{ ...glimpse.edge, transform: glimpse.edge.left === "50%" ? "translateX(-50%)" : undefined, filter: "blur(0.6px)" }}>{glimpse.text}</span>
      )}
      <FlashLayer active={active} cfg={layers?.flash} depthRef={depthRef} words={flash} />
    </div>
  );
}
